import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, ServerCog } from "lucide-react";

type Settings = {
  staging_scan_enabled: boolean;
  staging_scan_frequency: "daily" | "weekly";
  staging_scan_hour: number;
  staging_slack_webhook_url: string | null;
  staging_scan_last_run_at: string | null;
};

type Run = {
  id: string;
  environment: string;
  trigger_source: string;
  status: string;
  finding_count: number;
  new_count: number;
  new_finding_ids: string[];
  slack_status: string | null;
  slack_error: string | null;
  latency_ms: number | null;
  created_at: string;
};

/** Scheduled staging security scan: settings, run history and Slack delivery status. */
export function StagingSecurityScanPanel() {
  const [cfg, setCfg] = useState<Settings | null>(null);
  const [runs, setRuns] = useState<Run[]>([]);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  const load = async () => {
    const [{ data: s }, { data: r }] = await Promise.all([
      supabase
        .from("security_alert_settings")
        .select(
          "staging_scan_enabled, staging_scan_frequency, staging_scan_hour, staging_slack_webhook_url, staging_scan_last_run_at",
        )
        .eq("id", true)
        .maybeSingle(),
      supabase
        .from("security_staging_scan_runs")
        .select(
          "id, environment, trigger_source, status, finding_count, new_count, new_finding_ids, slack_status, slack_error, latency_ms, created_at",
        )
        .order("created_at", { ascending: false })
        .limit(25),
    ]);
    if (s) setCfg(s as Settings);
    setRuns((r as Run[]) ?? []);
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    const { error } = await supabase
      .from("security_alert_settings")
      .update({
        staging_scan_enabled: cfg.staging_scan_enabled,
        staging_scan_frequency: cfg.staging_scan_frequency,
        staging_scan_hour: cfg.staging_scan_hour,
        staging_slack_webhook_url: cfg.staging_slack_webhook_url?.trim() || null,
        updated_at: new Date().toISOString(),
      })
      .eq("id", true);
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success("Staging scan schedule saved");
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const { error } = await supabase.functions.invoke("security-staging-scan", {
        body: { environment: "staging", trigger_source: "manual", findings: [] },
        headers: session ? { Authorization: `Bearer ${session.access_token}` } : undefined,
      });
      if (error) throw error;
      toast.success("Staging scan run recorded");
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not run the staging scan");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-bold flex items-center gap-2">
            <ServerCog className="size-4 text-primary" />
            Scheduled staging security scan
          </h2>
          <p className="text-xs text-muted-foreground mt-1">
            Runs against staging on a schedule and posts a Slack summary only when new findings
            appear. Clean runs stay silent.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={runNow} disabled={running}>
          {running && <Loader2 className="size-3 animate-spin mr-1" />}
          Run staging scan now
        </Button>
      </div>

      {cfg && (
        <div className="grid gap-3 sm:grid-cols-4">
          <label className="flex items-center gap-2 text-xs">
            <Switch
              checked={cfg.staging_scan_enabled}
              onCheckedChange={(v) => setCfg({ ...cfg, staging_scan_enabled: v })}
            />
            Enabled
          </label>
          <label className="space-y-1">
            <span className="font-mono text-[10px] uppercase text-muted-foreground">Frequency</span>
            <Select
              value={cfg.staging_scan_frequency}
              onValueChange={(v) =>
                setCfg({ ...cfg, staging_scan_frequency: v as Settings["staging_scan_frequency"] })
              }
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
              </SelectContent>
            </Select>
          </label>
          <label className="space-y-1">
            <span className="font-mono text-[10px] uppercase text-muted-foreground">Hour (UTC)</span>
            <Input
              type="number"
              min={0}
              max={23}
              value={cfg.staging_scan_hour}
              onChange={(e) => setCfg({ ...cfg, staging_scan_hour: Number(e.target.value) })}
              className="h-8 text-xs"
            />
          </label>
          <label className="space-y-1">
            <span className="font-mono text-[10px] uppercase text-muted-foreground">
              Slack webhook (staging)
            </span>
            <Input
              value={cfg.staging_slack_webhook_url ?? ""}
              onChange={(e) => setCfg({ ...cfg, staging_slack_webhook_url: e.target.value })}
              placeholder="https://hooks.slack.com/services/…"
              className="h-8 text-xs"
            />
          </label>
        </div>
      )}

      <div className="flex items-center gap-3">
        <Button size="sm" onClick={save} disabled={saving || !cfg}>
          {saving && <Loader2 className="size-3 animate-spin mr-1" />}
          Save schedule
        </Button>
        {cfg?.staging_scan_last_run_at && (
          <span className="font-mono text-[10px] text-muted-foreground">
            last run {new Date(cfg.staging_scan_last_run_at).toLocaleString()}
          </span>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-muted-foreground font-mono text-[10px] uppercase">
            <tr className="border-b border-border">
              <th className="text-left py-2">When</th>
              <th className="text-left py-2">Trigger</th>
              <th className="text-left py-2">Status</th>
              <th className="text-right py-2">Findings</th>
              <th className="text-right py-2">New</th>
              <th className="text-left py-2">Slack</th>
            </tr>
          </thead>
          <tbody>
            {runs.length === 0 && (
              <tr>
                <td colSpan={6} className="py-4 text-muted-foreground">
                  No staging scan runs recorded yet.
                </td>
              </tr>
            )}
            {runs.map((r) => (
              <tr key={r.id} className="border-b border-border/50 align-top">
                <td className="py-2 whitespace-nowrap">
                  {new Date(r.created_at).toLocaleString()}
                </td>
                <td className="py-2 font-mono text-[10px]">{r.trigger_source}</td>
                <td className={`py-2 ${r.status === "ok" ? "" : "text-destructive"}`}>{r.status}</td>
                <td className="py-2 text-right font-mono">{r.finding_count}</td>
                <td className={`py-2 text-right font-mono ${r.new_count ? "text-destructive" : ""}`}>
                  {r.new_count}
                </td>
                <td className="py-2 font-mono text-[10px]">
                  {r.slack_status ?? "—"}
                  {r.slack_error && (
                    <span className="block text-destructive">{r.slack_error.slice(0, 80)}</span>
                  )}
                  {r.new_finding_ids?.length > 0 && (
                    <span className="block text-muted-foreground">
                      {r.new_finding_ids.slice(0, 4).join(", ")}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
